/**
 * Wave B2 (decision 0057): why a plan card goes to a person (`Proposal.asked`), stored apart from the proposer's
 * reasoning. `collapseAsked` bounds the list core emits; `askedText` is the agent-facing sentence (`read_proposal`).
 * People's words are the web's (`apps/web/src/model/card-words.ts`).
 */
import { agentOf, itemOf } from "./state"
import type { AskedReason, EntityRef, ProjectState } from "./types"

/** The schema's cap on `Proposal.asked`. */
export const ASKED_MAX = 12

const sameRef = (a: EntityRef | undefined, b: EntityRef | undefined): boolean =>
  a === undefined || b === undefined ? a === b : a._tag === b._tag && a.id === b.id

/**
 * One entry per rule + capability, in change order, with `asked` and `depends` after the rules that force a person
 * (they explain nothing on their own). `subject` / `userId` survive only when every reason of that rule + capability
 * names the same one. Sliced to 12 as a last guard (the schema's cap).
 */
export const collapseAsked = (reasons: ReadonlyArray<AskedReason>): ReadonlyArray<AskedReason> => {
  const groups = new Map<string, AskedReason[]>()
  for (const r of reasons) {
    const key = `${r.rule}|${r.capability ?? ""}`
    const g = groups.get(key)
    if (g) g.push(r)
    else groups.set(key, [r])
  }
  const out: AskedReason[] = []
  for (const g of groups.values()) {
    const first = g[0] as AskedReason
    const subject = g.every((r) => sameRef(r.subject, first.subject)) ? first.subject : undefined
    const userId = g.every((r) => r.userId === first.userId) ? first.userId : undefined
    out.push({
      rule: first.rule,
      ...(first.capability !== undefined ? { capability: first.capability } : {}),
      ...(subject !== undefined ? { subject } : {}),
      ...(userId !== undefined ? { userId } : {}),
    })
  }
  const late = (r: AskedReason) => r.rule === "asked" || r.rule === "depends"
  return [...out.filter((r) => !late(r)), ...out.filter(late)].slice(0, ASKED_MAX)
}

const refOf = (s: ProjectState | undefined, r: EntityRef | undefined): string | null => {
  if (!r) return null
  if (r._tag === "item") return (s && itemOf(s, r.id as never)?.ref) ?? null
  if (r._tag === "agent") {
    const a = s ? agentOf(s, r.id as never) : undefined
    return a ? `@${a.handle}` : null
  }
  return null
}

/** One reason in the words the agent hears (today's `why` strings). */
export const askedReasonText = (r: AskedReason, s?: ProjectState): string => {
  const ref = refOf(s, r.subject)
  const cap = r.capability ?? "this change"
  switch (r.rule) {
    case "cross_owner":
      return `${ref ?? "that agent"} belongs to another person, who approves changes to it`
    case "agent_level":
      return "an agent's level is set by a person"
    case "person_item":
      return `${ref ?? "these items"} ${ref ? "is a person's item" : "are a person's items"}: they approve changes to ${ref ? "it" : "them"}`
    case "person_spec":
      return `a person wrote ${ref ? `${ref}'s description` : "these descriptions"}: they approve changes to ${ref ? "it" : "them"}`
    case "brief":
      return "a person approves every change to the brief"
    case "ask_first":
      return "Ask first: a turn changes started proposes every change"
    case "frozen_checks":
      return `${ref ? `${ref}'s checks are` : "the checks are"} frozen: changing them needs a person`
    case "policy":
      return `${cap} needs approval`
    case "policy_limit":
      return `${cap}: over the policy's limit`
    case "no_policy":
      return `${cap} is not allowed by any policy`
    case "asked":
      return "you chose to ask first"
    case "depends":
      return "depends on a proposed change"
  }
}

/** "the brief is the project owner's: …; new items need approval" (null when there is nothing to say). */
export const askedText = (asked: ReadonlyArray<AskedReason> | undefined, s?: ProjectState): string | null => {
  if (!asked || asked.length === 0) return null
  return [...new Set(asked.map((r) => askedReasonText(r, s)))].join("; ")
}
